import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

// Pages
import LoginPage from './pages/UserManagement/LoginPage';
import RegisterPage from './pages/UserManagement/RegisterPage';
import RegisterSuccessPage from './pages/UserManagement/RegisterSuccessPage';
import PasswordResetPage from './pages/UserManagement/PasswordResetPage';
import LandingPage from './pages/LandingPage/LandingPage';
import SettingsPage from './pages/UserManagement/Settings';
import MicroLearning from './pages/MircoLearning/MicroLearning';
import CategoryForm from './pages/CategoryForm/CategoryForm';
import PrivateRoute from './components/authentication/PrivateRoute';
import HomePage from './pages/HomePage/HomePage';
import Impressum from './pages/Impressum/Impressum';
import Datenschutz from './pages/Datenschutz/Datenschutz';
import AGBPage from './pages/AGB/AGBPage';
import EmailVerificationPage from './pages/UserManagement/EmailVerificationPage';
import ResetPasswordConfirmPage from './pages/UserManagement/ResetPasswordConfirmPage';
import WikiPage from './pages/Wiki/WikiPage';
import ExamOverview from './pages/ExamOverview/ExamOverview';
import WikiHome from './pages/Wiki/WikiHome';

// Quiz Pages
import MicroLearningQuiz from './pages/Quiz/MicroLearningQuiz';
import SpacedLearningQuiz from './pages/Quiz/SpacedLearningQuiz';
import ExamQuiz from './pages/Quiz/ExamQuiz';

// Admin Pages
import AdminPage from './pages/Admin/AdminPage';
import QuestionForm from './pages/Admin/Questions/QuestionForm';
import QuestionListAdmin from './pages/Admin/Questions/QuestionList';
import QuestionCreateForm from './pages/Admin/Questions/QuestionCreateForm';
import QuestionEditForm from './pages/Admin/Questions/QuestionEditForm';
import ExamCreateForm from './pages/Admin/Exams/ExamCreateForm';
import ExamSubcategoryCreateForm from './pages/Admin/Exams/ExamSubcategoryCreateForm';
import ExamList from './pages/Admin/Exams/ExamList';
import ExamSubcategoryList from './pages/Admin/Exams/ExamSubcategoryList';
import ExamEditForm from './pages/Admin/Exams/ExamEditForm';
import ExamSubcategoryEditForm from './pages/Admin/Exams/ExamSubcategoryEditForm';
import ExamQuestionMapping from './pages/Admin/Exams/ExamQuestionMapping';

// Context
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import QuestionList from './pages/QuestionList/QuestionList';
import { AlertProvider } from './context/AlertContext';
import CheckoutSimulator from './pages/CheckoutSimulator/CheckoutSimulator';
import PaymentSuccess from './pages/PaymentSuccess/PaymentSuccess';

const App = () => {
  return (
    <AlertProvider>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              {/* User Management Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/register-success" element={<RegisterSuccessPage />} />
              <Route path="/password-reset" element={<PasswordResetPage />} />
              <Route path="/password-reset/:token" element={<ResetPasswordConfirmPage />} />
              <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
              <Route path="/settings" element={<SettingsPage />} />

              {/* Home Page */}
              <Route path="/home" element={<PrivateRoute element={HomePage} />} />

              {/* Theoriefragen */}
              <Route path="/alle-fragen" element={<PrivateRoute element={QuestionList} />} />
              <Route path="/micro-learning" element={<PrivateRoute element={MicroLearning} />} />
              <Route path="/micro-learning/quiz/:catalogId" element={<PrivateRoute element={MicroLearningQuiz} />} />

              {/* Question Management */}
              <Route path="/question-entry" element={<PrivateRoute element={QuestionForm} />} />
              <Route path="/topic-form" element={<PrivateRoute element={CategoryForm} />} />


              {/* Exam Papers */}
              <Route path="/pruefungsboegen" element={<PrivateRoute element={ExamOverview} />} />
              <Route path="/pruefungsboegen/quiz/:subcategoryId" element={<PrivateRoute element={ExamQuiz} />} />

              {/* Impressum */}
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/agb" element={<AGBPage />} />

              {/* Quiz */}
              <Route path="/spaced-learning" element={<PrivateRoute element={SpacedLearningQuiz} />} />


              {/* Wiki */}
              <Route path="/wiki" element={<WikiHome />} />
              <Route path="/wiki/:slug" element={<WikiPage />} />

              {/* Admin */}
              <Route path="/admin" element={<PrivateRoute element={AdminPage} />} />

              {/* Admin Question Routes */}
              <Route path="/admin/questions" element={<PrivateRoute element={QuestionListAdmin} />} />
              <Route path="/admin/questions/add" element={<PrivateRoute element={QuestionCreateForm} />} />
              <Route path="/admin/questions/:id/edit" element={<PrivateRoute element={QuestionEditForm} />} />

              {/* Admin Category Routes */}
              <Route path="/admin/categories" element={<PrivateRoute element={AdminPage} />} />
              <Route path="/admin/categories/add" element={<PrivateRoute element={AdminPage} />} />
              <Route path="/admin/categories/:id" element={<PrivateRoute element={AdminPage} />} />

              {/* Admin Catalog Routes */}
              <Route path="/admin/catalogs" element={<PrivateRoute element={AdminPage} />} />
              <Route path="/admin/catalogs/add" element={<PrivateRoute element={AdminPage} />} />
              <Route path="/admin/catalogs/:id" element={<PrivateRoute element={AdminPage} />} />

              {/* Admin Exam Routes */}
              <Route path="/admin/exams" element={<PrivateRoute element={ExamList} />} />
              <Route path="/admin/exams/add" element={<PrivateRoute element={ExamCreateForm} />} />
              <Route path="/admin/exams/:id/edit" element={<PrivateRoute element={ExamEditForm} />} />

              {/* Admin Exam Parts Routes */}
              <Route path="/admin/exam-parts" element={<PrivateRoute element={ExamSubcategoryList} />} />
              <Route path="/admin/exam-parts/add" element={<PrivateRoute element={ExamSubcategoryCreateForm} />} />
              <Route path="/admin/exam-parts/:id/edit" element={<PrivateRoute element={ExamSubcategoryEditForm} />} />
              <Route path="/admin/mapping/exam" element={<PrivateRoute element={ExamQuestionMapping} />} />

              {/* Checkout Simulator */}
              <Route path="/checkout-simulator" element={<CheckoutSimulator />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />

              {/* 404 Page */}
              <Route path="*" element={<div>404 Page</div>} />
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </AlertProvider>
  );
};

export default App;
